import React, {useEffect, useState} from 'react';
import {View} from 'react-native';

import Carousel from '/component/base/Carousel';
import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import Gutter from '/component/layout/Gutter/Gutter';
import {DashboardID} from '/constant/TestIDs';
import {useTranslation} from '/provider/TranslationProvider';
import {enableLayoutAnimation, safeLayoutAnimation} from '/util/animation.util';
import testProperties from '/util/testing/testProperties';

import {CoachAcceleratorBonusBanner} from './AcceleratorBanner';
import {styles} from './BonusBannerCards.style';
import {BonusBannerCardsProps} from './BonusBannerCards.types';
import {BusinessBuilderBonusBanner} from './BusinessBuilderBonusBanner';
import {CSBPlusBanner} from './CSBPlusBanner';
import {IncentiveTripBanner} from './IncentiveTripBanner';
import {TextOnlyBanner} from './TextOnlyBanner';

export const BonusBannerCards = ({
  showIncentiveTrip = false,
  showAccelerator = false,
  showCSBPlus = false,
  getAcceleratorBonusBanner = () => {},
  hasAcceleratorBonusData = false,
  getCSBPlusBanner = () => {},
  hasCSBPlusData = false,
  popoverShow = () => {},
  parentScreenName = '',
  getIncentiveTripBanner = () => {},
  hasIncentiveTripData = false,
  featureFlag = {
    showIncentiveUpdate: false,
    showIncentiveTrip: false,
    showBusinessBuilderBonusBanner: false,
    showIncentiveTextBanner: false,
  },
  isQualified = false,
  hasBusinessBuilderBonusBanner = false,
  showBusinessBuilderBonusBanner = false,
}: BonusBannerCardsProps) => {
  const {ts, tt} = useTranslation('bonusesBanner');
  const [bonusExpanded, setBonusExpanded] = useState(true);
  const [bannerHeight, setBannerHeight] = useState<number | null>(null);

  useEffect(() => {
    if (!hasAcceleratorBonusData) {
      getAcceleratorBonusBanner();
    }
    if (!hasCSBPlusData) {
      getCSBPlusBanner();
    }
    if (featureFlag.showIncentiveTrip && !hasIncentiveTripData) {
      getIncentiveTripBanner();
    }
  }, [
    hasAcceleratorBonusData,
    getAcceleratorBonusBanner,
    hasCSBPlusData,
    getCSBPlusBanner,
    getIncentiveTripBanner,
    hasIncentiveTripData,
    featureFlag.showIncentiveTrip,
    featureFlag.showBusinessBuilderBonusBanner,
    hasBusinessBuilderBonusBanner,
  ]);

  useEffect(() => {
    enableLayoutAnimation();
  }, []);

  useEffect(() => {
    setBannerHeight(null);
  }, [showAccelerator, showCSBPlus, showIncentiveTrip, bonusExpanded]);

  const toggleExpand = () => {
    safeLayoutAnimation();
    setBonusExpanded(!bonusExpanded);
  };

  const handleBannerHeight = (event: any) => {
    let newHeight = Math.round(event.nativeEvent.layout.height);

    if (!showBusinessBuilderBonusBanner && !showCSBPlus) {
      newHeight += 15;
    }

    setBannerHeight(prevHeight =>
      !prevHeight || prevHeight < newHeight ? newHeight : prevHeight,
    );
  };

  const generateBannerCards = () => {
    const cards = [];
    if (hasBusinessBuilderBonusBanner) {
      cards.push((cardNumber: any, totalCards: any) => (
        <BusinessBuilderBonusBanner
          popoverShow={popoverShow}
          cardNumber={cardNumber}
          totalCards={totalCards}
          bannerHeight={bannerHeight}
          handleBannerHeight={handleBannerHeight}
          title={'Business Builder Bonus'}
          isBanner
        />
      ));
    }
    if (showCSBPlus) {
      cards.push((cardNumber: any, totalCards: any) => (
        <CSBPlusBanner
          popoverShow={popoverShow}
          cardNumber={cardNumber}
          totalCards={totalCards}
          bannerHeight={bannerHeight}
          handleBannerHeight={handleBannerHeight}
        />
      ));
    }
    if (showAccelerator) {
      cards.push((cardNumber: any, totalCards: any) => (
        <CoachAcceleratorBonusBanner
          popoverShow={popoverShow}
          cardNumber={cardNumber}
          totalCards={totalCards}
          bannerHeight={bannerHeight}
          handleBannerHeight={handleBannerHeight}
        />
      ));
    }
    if (showIncentiveTrip) {
      cards.push((cardNumber: any, totalCards: any) => {
        return (
          <IncentiveTripBanner
            popoverShow={popoverShow}
            parentScreenName={parentScreenName}
            cardNumber={cardNumber}
            totalCards={totalCards}
            bannerHeight={bannerHeight}
            handleBannerHeight={handleBannerHeight}
            isQualified={isQualified}
          />
        );
      });
    }
    if (featureFlag.showIncentiveTextBanner) {
      cards.push((cardNumber: any, totalCards: any) => {
        return (
          <TextOnlyBanner
            popoverShow={popoverShow}
            parentScreenName={parentScreenName}
            cardNumber={cardNumber}
            totalCards={totalCards}
            bannerHeight={bannerHeight}
          />
        );
      });
    }
    return cards;
  };

  const renderBonuses = () => {
    const cards = generateBannerCards();
    if (cards.length === 1) {
      const Card = cards[0];
      // @ts-expect-error TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
      return <Gutter style={styles.singleCard}>{Card()}</Gutter>;
    }
    if (cards.length > 1) {
      const cardTitles = [
        'Business Builder Bonus',
        'CSB Plus Bonus',
        'Coach Accelerator Bonus',
        'Incentive Trip',
        'Text Only',
      ].slice(0, cards.length);

      return (
        <Carousel
          cardTitles={cardTitles}
          webFitContainer={true}
          restrictAccessibilityToActiveSlide={true}>
          {cards.map((card, i) => {
            const Card = card;
            const cardNumber = i + 1;
            const totalCards = cards.length;

            return (
              <React.Fragment key={i}>
                {Card(cardNumber, totalCards)}
              </React.Fragment>
            );
          })}
        </Carousel>
      );
    }
  };

  return (
    <View
      style={styles.container}
      {...testProperties(DashboardID.dashboardBonusesView)}>
      <View
        style={styles.header}
        {...testProperties(DashboardID.dashboardBonusesHeader)}>
        <Text
          allowFontScaling
          maxFontSizeMultiplier={2}
          variant="heading"
          style={styles.headerText}
          accessible={true}
          accessibilityRole="header"
          {...testProperties(DashboardID.dashboardBonusesLabel)}>
          {tt('heading', 'Active business incentives')}
        </Text>

        <TouchableOpacity
          onPress={toggleExpand}
          style={styles.showHideButtonContainer}
          accessibilityRole="button"
          accessibilityState={{expanded: bonusExpanded}}
          accessibilityLabel={`Active business incentives. ${
            bonusExpanded ? 'Hide' : 'Show'
          }`}
          {...testProperties(DashboardID.dashboardBonusesShowHideButton, true)}>
          <Text
            allowFontScaling
            maxFontSizeMultiplier={2}
            accessible={true}
            variant="body"
            disabled
            accessibilityElementsHidden={true} // Hides from VoiceOver
            importantForAccessibility="no-hide-descendants"
            style={styles.showHideButton}
            {...testProperties(DashboardID.dashboardBonusesShowHideText)}>
            {bonusExpanded ? ts('hide', 'Hide') : ts('show', 'Show')}
          </Text>
          <Icon
            accessible={true}
            accessibilityLabel="button"
            accessibilityElementsHidden={true}
            importantForAccessibility="no-hide-descendants"
            name={bonusExpanded ? 'chevron-up' : 'chevron-down'}
            style={styles.icon}
            {...testProperties(DashboardID.dashboardBonusesShowHideIcon)}
          />
        </TouchableOpacity>
      </View>
      {bonusExpanded && renderBonuses()}
    </View>
  );
};
